/**
 * Thin composer growth: decides when the one-row docked composer (+, text, send
 * side by side) opens into the grown layout (text on top, controls under it).
 *
 * The thin textarea is fixed at `text-base leading-6` with `py-1.5`, so the
 * one-line height is a constant (24 + 12). The box grows once the text needs
 * more than half a second line, on a newline, with attachments, or as the hero.
 * Growth latches: a grown box only returns to the row once it is empty and has
 * no attachments, so the layout never flip-flops at the wrap point.
 */

import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/** Line height of the thin textarea (`leading-6`). */
export const THIN_LINE_HEIGHT_PX = 24
/** Vertical padding of the thin textarea (`py-1.5`, top plus bottom). */
export const THIN_TEXTAREA_PADDING_Y_PX = 12
/** A row textarea taller than this has wrapped onto a second line. */
export const THIN_GROW_THRESHOLD_PX =
  THIN_LINE_HEIGHT_PX + THIN_TEXTAREA_PADDING_Y_PX + THIN_LINE_HEIGHT_PX / 2

export interface ComposerGrowthInput {
  isHero: boolean
  hasAttachments: boolean
  value: string
  wasGrown: boolean
  /** The textarea's scrollHeight while it sits in the row. */
  rowScrollHeightPx: number
}

export function shouldComposerGrow(input: ComposerGrowthInput): boolean {
  const { isHero, hasAttachments, value, wasGrown, rowScrollHeightPx } = input
  if (isHero || hasAttachments) {
    return true
  }
  if (value === '') {
    return false
  }
  return wasGrown || value.includes('\n') || rowScrollHeightPx > THIN_GROW_THRESHOLD_PX
}

export interface UseComposerGrowthOptions {
  /** False outside the thin density: the hook then always reports the row and never measures. */
  isEnabled: boolean
  textareaRef: RefObject<HTMLTextAreaElement | null>
  /** The grid holding the textarea and the controls; its width is watched in the row. */
  containerRef: RefObject<HTMLElement | null>
  value: string
  isHero: boolean
  hasAttachments: boolean
}

/**
 * Runs in a layout effect (declare it after `useAutosizeTextarea`), so the switch
 * lands before paint. In the row with text, a ResizeObserver on the container's
 * width re-measures, since a narrower row can wrap text that fit before.
 */
export function useComposerGrowth(options: UseComposerGrowthOptions): boolean {
  const { isEnabled, textareaRef, containerRef, value, isHero, hasAttachments } = options
  const [isGrown, setIsGrown] = useState(false)
  const isGrownRef = useRef(isGrown)
  isGrownRef.current = isGrown
  // Latest inputs live in refs so `evaluate` stays stable and the row's
  // ResizeObserver is built once per row session, not once per keystroke.
  const isEnabledRef = useRef(isEnabled)
  isEnabledRef.current = isEnabled
  const valueRef = useRef(value)
  valueRef.current = value
  const isHeroRef = useRef(isHero)
  isHeroRef.current = isHero
  const hasAttachmentsRef = useRef(hasAttachments)
  hasAttachmentsRef.current = hasAttachments

  const evaluate = useCallback(() => {
    if (!isEnabledRef.current) {
      return
    }
    const wasGrown = isGrownRef.current
    const currentValue = valueRef.current
    const el = textareaRef.current
    // Only the row is measured: a grown box latches, so its height never decides.
    const rowScrollHeightPx = !wasGrown && currentValue !== '' && el ? el.scrollHeight : 0
    const next = shouldComposerGrow({
      isHero: isHeroRef.current,
      hasAttachments: hasAttachmentsRef.current,
      value: currentValue,
      wasGrown,
      rowScrollHeightPx,
    })
    if (next !== wasGrown) {
      setIsGrown(next)
    }
  }, [textareaRef])

  useLayoutEffect(() => {
    evaluate()
  }, [evaluate, isEnabled, value, isHero, hasAttachments])

  const shouldObserve = isEnabled && !isGrown && value !== ''
  useEffect(() => {
    if (!shouldObserve || typeof ResizeObserver === 'undefined') {
      return
    }
    const container = containerRef.current
    if (!container) {
      return
    }
    let lastWidth = container.getBoundingClientRect().width
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? lastWidth
      if (width === lastWidth) {
        return
      }
      lastWidth = width
      evaluate()
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [shouldObserve, containerRef, evaluate])

  // The forced cases answer on the first render too, so a hero never paints as a row.
  return isEnabled && (isGrown || isHero || hasAttachments)
}
